import { ethers } from 'ethers';

// TokenLocker Contract ABI
export const TOKENLOCKER_ABI = [
  // Events
  "event TokenLocked(uint256 indexed lockId, address indexed user, address indexed token, uint256 amount, uint256 unlockDate, uint256 fee)",
  "event TokenWithdrawn(uint256 indexed lockId, address indexed user)",
  "event LockTransferred(uint256 indexed lockId, address indexed from, address indexed to)",
  "event LockFeeUpdated(uint256 oldFee, uint256 newFee)",
  "event FeesWithdrawn(uint256 amount, address recipient)",
  
  // State variables
  "function nextLockId() view returns (uint256)",
  "function lockFee() view returns (uint256)",
  
  // Lock management
  "function lockTokens(address token, uint256 amount, uint256 unlockDate) payable",
  "function withdraw(uint256 lockId)",
  "function transferLock(uint256 lockId, address newOwner)",
  
  // View functions
  "function locks(uint256 lockId) view returns (uint256 id, address token, address owner, uint256 amount, uint256 unlockDate, bool withdrawn)",
  "function getUserLocks(address user) view returns (uint256[])",
  "function getTokenLocks(address token) view returns (uint256[])",
  "function getLock(uint256 lockId) view returns (uint256 id, address token, address owner, uint256 amount, uint256 unlockDate, bool withdrawn)",
  "function getLockedAmount(address user, address token) view returns (uint256 total)",
  "function getActiveLocks(address user) view returns (tuple(uint256 id, address token, address owner, uint256 amount, uint256 unlockDate, bool withdrawn)[])",
  "function getLockFee() view returns (uint256)",
  "function getAccumulatedFees() view returns (uint256)",
  
  // Owner functions
  "function updateLockFee(uint256 newFee)",
  "function withdrawFees()"
];

// ERC20 Token ABI
export const ERC20_ABI = [
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function totalSupply() view returns (uint256)",
  "function balanceOf(address account) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function transfer(address to, uint256 amount) returns (bool)",
  "function transferFrom(address from, address to, uint256 amount) returns (bool)",
  "event Transfer(address indexed from, address indexed to, uint256 value)",
  "event Approval(address indexed owner, address indexed spender, uint256 value)"
];

// Contract addresses (you'll need to update these with your deployed contract addresses)
export const CONTRACT_ADDRESSES = {  
  TOKENLOCKER: '0x824d55a35a0cF85Bb2469AAC8D2fF9aEa4211a2A',   
};

// Network configuration
export const NETWORKS = {
  // Katana only
  KATANA: {
    chainId: 747474,
    chainIdHex: '0xB67D2',
    name: 'Katana',
    rpcUrl: 'https://rpc.katana.network',
    currency: 'ETH'
  }
};

export const KATANA_NETWORK = NETWORKS.KATANA;

// Helper function to get contract instance
export const getContract = (address: string, abi: any, signerOrProvider: ethers.Signer | ethers.Provider) => {
  return new ethers.Contract(address, abi, signerOrProvider);
};

// Helper function to format token amounts
export const formatTokenAmount = (
  amount: bigint,
  decimals: number | bigint = 18
): string => {
  const decimalsNum = typeof decimals === 'bigint'
    ? Number(decimals)
    : typeof decimals === 'number'
      ? decimals
      : 18;
  const divisor = 10n ** BigInt(decimalsNum);
  const whole = amount / divisor;
  const fraction = amount % divisor;
  
  if (fraction === 0n) {
    return whole.toString();
  }
  
  const fractionStr = fraction.toString().padStart(Number(decimalsNum), '0');
  const trimmedFraction = fractionStr.replace(/0+$/, '');
  
  return `${whole}.${trimmedFraction}`;
};

// Helper function to parse token amounts
export const parseTokenAmount = (amount: string, decimals: number = 18): bigint => {
  const [whole, fraction = ''] = amount.split('.');
  const paddedFraction = fraction.padEnd(decimals, '0').slice(0, decimals);
  return BigInt(whole + paddedFraction);
};

// Helper function to format date
export const formatDate = (timestamp: bigint): string => {
  const date = new Date(Number(timestamp) * 1000);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Helper function to check if lock can be withdrawn
export const canWithdraw = (unlockDate: bigint, withdrawn: boolean): boolean => {
  const now = BigInt(Math.floor(Date.now() / 1000));
  return !withdrawn && now >= unlockDate;
};

// Helper function to get lock status
export const getLockStatus = (unlockDate: bigint, withdrawn: boolean): 'Locked' | 'Unlocked' | 'Withdrawn' => {
  if (withdrawn) return 'Withdrawn';
  const now = BigInt(Math.floor(Date.now() / 1000));
  return now >= unlockDate ? 'Unlocked' : 'Locked';
};
